import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ProjectSpendMonth } from "../api/projects";
import { formatEuro, formatMonthYear, toNumber } from "../lib/format";

import Spinner from "./Spinner";

type Point = {
  month_start: string;
  timestamp: number;
  monthly: number;
  cumulative: number;
};

type Props = {
  items: ProjectSpendMonth[];
  isLoading?: boolean;
  isError?: boolean;
  errorLabel?: string;
};

export default function ProjectSpendChart({
  items,
  isLoading = false,
  isError = false,
  errorLabel = "Ausgaben-Daten konnten nicht geladen werden.",
}: Props) {
  const points = useMemo<Point[]>(() => {
    const out: Point[] = [];
    for (const row of items) {
      if (!row.month_start) continue;
      const ts = Date.parse(row.month_start);
      if (!Number.isFinite(ts)) continue;
      const monthly = toNumber(row.total_spend) ?? 0;
      const cumulative = toNumber(row.cumulative_spend) ?? 0;
      out.push({
        month_start: row.month_start.slice(0, 10),
        timestamp: ts,
        monthly,
        cumulative,
      });
    }
    out.sort((a, b) => a.timestamp - b.timestamp);
    return out;
  }, [items]);

  if (isLoading) {
    return (
      <div className="flex h-80 w-full items-center justify-center rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <Spinner label="Lade Ausgaben-Daten …" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-80 w-full items-center justify-center rounded-lg border border-rose-200 bg-rose-50 px-4 text-center text-sm text-rose-700">
        {errorLabel}
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="flex h-80 w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-4 text-center text-sm text-slate-500">
        Keine monatlichen Ausgaben-Daten.
      </div>
    );
  }

  return (
    <div className="h-80 w-full rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={points}
          margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
        >
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
          <XAxis
            dataKey="month_start"
            tickFormatter={(v: string) => formatMonthYear(v)}
            stroke="#64748b"
            fontSize={12}
            minTickGap={24}
          />
          <YAxis
            yAxisId="left"
            stroke="#64748b"
            fontSize={12}
            tickFormatter={(v: number) => formatEuro(v)}
            width={80}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#94a3b8"
            fontSize={12}
            tickFormatter={(v: number) => formatEuro(v)}
            width={80}
          />
          <Tooltip
            labelFormatter={(label) => formatMonthYear(String(label))}
            formatter={(value, name) => [
              formatEuro(value as number),
              name === "monthly" ? "Monat" : "Kumulativ",
            ]}
            contentStyle={{ fontSize: 12 }}
          />
          <Bar
            yAxisId="left"
            dataKey="monthly"
            fill="#0f766e"
            name="monthly"
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="cumulative"
            stroke="#f97316"
            strokeWidth={2}
            dot={false}
            name="cumulative"
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
